import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Maximize2, LocateFixed, X } from 'lucide-react';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';
import { uberMapStyle } from '../../styles/mapStyle';
import CommandCenterMapLegend from './CommandCenterMapLegend';
import ambulancePng from '../../svg/Rectangle.png';
import { makePinSvg } from '../../utils/mapMarkers';

const DRIVER_ICON_SIZE = { normal: 36, selected: 56 };
const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 }; // Ahmedabad

// Static map card (not a modal) for the booking page.
// Markers and Polyline route use native imperative Google Maps API objects
// for 100% reliable rendering across React portal fullscreen transitions.
const CommandCenterBookingMapCard = ({ pickupCoordinates, dropoffCoordinates, drivers, selectedDriverId, onDriverMarkerClick, routePath, distanceKm, durationMin }) => {
  const { isLoaded } = useJsApiLoader({ ...googleMapsLoaderConfig });
  const [map, setMap] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const driverMarkersRef = useRef({});
  const polylineRef = useRef(null);
  const glowPolylineRef = useRef(null);
  const userInteractedRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);
  const programmaticTimerRef = useRef(null);
  const prevCoordsRef = useRef({ pickupLat: null, pickupLng: null, dropoffLat: null, dropoffLng: null });

  const getLat = (coord) => (coord?.latitude != null ? parseFloat(coord.latitude) : coord?.lat != null ? parseFloat(coord.lat) : null);
  const getLng = (coord) => (coord?.longitude != null ? parseFloat(coord.longitude) : coord?.lng != null ? parseFloat(coord.lng) : null);

  const normalizedRoutePath = useMemo(() => {
    if (!Array.isArray(routePath) || routePath.length === 0) return [];
    return routePath
      .map((pt) => ({ lat: getLat(pt), lng: getLng(pt) }))
      .filter((pt) => pt.lat != null && pt.lng != null && !isNaN(pt.lat) && !isNaN(pt.lng));
  }, [routePath]);

  const applyBoundsToMap = (mapInstance = mapInstanceRef.current || map) => {
    if (!mapInstance || !window.google?.maps) return;
    const points = [];
    const pLat = getLat(pickupCoordinates);
    const pLng = getLng(pickupCoordinates);
    if (pLat != null && pLng != null && !isNaN(pLat) && !isNaN(pLng)) {
      points.push({ lat: pLat, lng: pLng });
    }

    const dLat = getLat(dropoffCoordinates);
    const dLng = getLng(dropoffCoordinates);
    if (dLat != null && dLng != null && !isNaN(dLat) && !isNaN(dLng)) {
      points.push({ lat: dLat, lng: dLng });
    }

    (drivers || [])
      .forEach((d) => {
        const dLatVal = getLat(d.location);
        const dLngVal = getLng(d.location);
        if (dLatVal != null && dLngVal != null && !isNaN(dLatVal) && !isNaN(dLngVal)) {
          points.push({ lat: dLatVal, lng: dLngVal });
        }
      });

    // If route path exists, also sample key points into bounds to ensure complete curve fits
    if (normalizedRoutePath.length > 0) {
      points.push(normalizedRoutePath[0]);
      points.push(normalizedRoutePath[Math.floor(normalizedRoutePath.length / 2)]);
      points.push(normalizedRoutePath[normalizedRoutePath.length - 1]);
    }

    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    isProgrammaticMoveRef.current = true;
    if (points.length === 0) {
      mapInstance.setCenter(DEFAULT_CENTER);
      mapInstance.setZoom(12);
    } else if (points.length === 1) {
      mapInstance.setCenter(points[0]);
      mapInstance.setZoom(14);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      mapInstance.fitBounds(bounds, { padding: 60 });
    }

    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, 800);
  };

  const onMapLoad = (mapInstance) => {
    mapInstanceRef.current = mapInstance;
    setMap(mapInstance);
    // Allow DOM layout to settle, then fit bounds and trigger resize
    setTimeout(() => {
      if (window.google?.maps && mapInstance) {
        window.google.maps.event.trigger(mapInstance, 'resize');
        applyBoundsToMap(mapInstance);
      }
    }, 100);
  };

  const onMapUnmount = () => {
    mapInstanceRef.current = null;
    setMap(null);
  };

  // Google Maps doesn't auto-redraw when its container resizes (e.g. going
  // compact <-> fullscreen), so reset interaction lock and trigger resize + bounds.
  useEffect(() => {
    userInteractedRef.current = false;

    const timers = [
      setTimeout(() => {
        const liveMap = mapInstanceRef.current || map;
        if (liveMap && window.google?.maps) {
          window.google.maps.event.trigger(liveMap, 'resize');
          applyBoundsToMap(liveMap);
        }
      }, 80),
      setTimeout(() => {
        const liveMap = mapInstanceRef.current || map;
        if (liveMap && window.google?.maps) {
          window.google.maps.event.trigger(liveMap, 'resize');
          applyBoundsToMap(liveMap);
        }
      }, 250),
      setTimeout(() => {
        const liveMap = mapInstanceRef.current || map;
        if (liveMap && window.google?.maps) {
          window.google.maps.event.trigger(liveMap, 'resize');
          applyBoundsToMap(liveMap);
        }
      }, 500),
    ];

    return () => timers.forEach(clearTimeout);
  }, [map, isFullscreen]);

  // Keep map tiles rendered when container resizes or scrolls (prevents blank/grey screen)
  useEffect(() => {
    if (!map) return undefined;
    const container = mapContainerRef.current;
    if (!container) return undefined;

    let rafId = null;
    const triggerResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const liveMap = mapInstanceRef.current || map;
        if (liveMap && window.google?.maps) {
          window.google.maps.event.trigger(liveMap, 'resize');
        }
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      triggerResize();
    });
    resizeObserver.observe(container);

    window.addEventListener('scroll', triggerResize, { passive: true });
    const scrollParent = container.closest('.overflow-y-auto') || container.closest('main');
    scrollParent?.addEventListener('scroll', triggerResize, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', triggerResize);
      scrollParent?.removeEventListener('scroll', triggerResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [map]);

  // Imperative native Polyline drawing:
  // Rendered directly on map instance, guaranteeing 100% reliable display in both
  // compact card mode and fullscreen modal portal.
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (glowPolylineRef.current) {
      glowPolylineRef.current.setMap(null);
      glowPolylineRef.current = null;
    }

    if (normalizedRoutePath.length === 0) return undefined;

    // Glow / shadow polyline
    glowPolylineRef.current = new window.google.maps.Polyline({
      map,
      path: normalizedRoutePath,
      strokeColor: '#1E40AF',
      strokeOpacity: 0.28,
      strokeWeight: 10,
      geodesic: true,
      zIndex: 895,
    });

    // Main vibrant blue driving route with forward directional arrows
    const arrowSymbol = window.google.maps.SymbolPath?.FORWARD_CLOSED_ARROW ?? 0;
    polylineRef.current = new window.google.maps.Polyline({
      map,
      path: normalizedRoutePath,
      strokeColor: '#1A73E8',
      strokeOpacity: 1,
      strokeWeight: 5,
      geodesic: true,
      zIndex: 898,
      icons: [
        {
          icon: {
            path: arrowSymbol,
            fillColor: '#1A73E8',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 1.5,
            scale: 3,
          },
          offset: '25px',
          repeat: '65px',
        },
      ],
    });

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (glowPolylineRef.current) {
        glowPolylineRef.current.setMap(null);
        glowPolylineRef.current = null;
      }
    };
  }, [map, normalizedRoutePath]);

  // Frame route once route path arrives, if user hasn't manually interacted
  useEffect(() => {
    if (!map || !window.google?.maps || normalizedRoutePath.length === 0) return;
    if (!userInteractedRef.current) {
      applyBoundsToMap(map);
    }
  }, [map, normalizedRoutePath]);

  // Pickup/dropoff markers: Matching identical Google Maps pin style (blue for pickup, red for dropoff)
  // zIndex: 1000 ensures pickup/dropoff pins are always visible above driver ambulance icons (zIndex: 920)
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    const pLat = getLat(pickupCoordinates);
    const pLng = getLng(pickupCoordinates);
    if (pLat != null && pLng != null && !isNaN(pLat) && !isNaN(pLng)) {
      pickupMarkerRef.current = new window.google.maps.Marker({
        map,
        position: { lat: pLat, lng: pLng },
        title: `Pickup: ${pickupCoordinates.address || pickupCoordinates.name || ''}`,
        icon: {
          url: makePinSvg('blue'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 1000,
        optimized: false,
      });
    }

    const dLat = getLat(dropoffCoordinates);
    const dLng = getLng(dropoffCoordinates);
    if (dLat != null && dLng != null && !isNaN(dLat) && !isNaN(dLng)) {
      dropoffMarkerRef.current = new window.google.maps.Marker({
        map,
        position: { lat: dLat, lng: dLng },
        title: `Drop-off: ${dropoffCoordinates.address || dropoffCoordinates.name || ''}`,
        icon: {
          url: makePinSvg('red'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 1000,
        optimized: false,
      });
    }

    return () => {
      pickupMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current?.setMap(null);
      pickupMarkerRef.current = null;
      dropoffMarkerRef.current = null;
    };
  }, [map, pickupCoordinates, dropoffCoordinates]);

  // Driver markers
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    const markers = drivers
      .filter((driver) => driver.location?.latitude != null && driver.location?.longitude != null)
      .map((driver) => {
        const marker = new window.google.maps.Marker({
          map,
          position: { lat: driver.location.latitude, lng: driver.location.longitude },
          title: driver.name,
          icon: {
            url: ambulancePng,
            scaledSize: new window.google.maps.Size(DRIVER_ICON_SIZE.normal, DRIVER_ICON_SIZE.normal),
            anchor: new window.google.maps.Point(DRIVER_ICON_SIZE.normal / 2, DRIVER_ICON_SIZE.normal / 2),
          },
          zIndex: 920,
          optimized: false,
        });
        marker.addListener('click', () => onDriverMarkerClick?.(driver));
        return [driver.id, marker];
      });

    driverMarkersRef.current = Object.fromEntries(markers);

    return () => {
      markers.forEach(([, marker]) => marker.setMap(null));
      driverMarkersRef.current = {};
    };
  }, [map, drivers, onDriverMarkerClick]);

  // Update icon size in place on selection change
  useEffect(() => {
    if (!window.google?.maps) return;
    Object.entries(driverMarkersRef.current).forEach(([driverId, marker]) => {
      const isSelected = driverId === selectedDriverId;
      const size = isSelected ? DRIVER_ICON_SIZE.selected : DRIVER_ICON_SIZE.normal;
      marker.setIcon({
        url: ambulancePng,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2),
      });
      marker.setOpacity(isSelected || !selectedDriverId ? 1 : 0.55);
      marker.setZIndex(isSelected ? 950 : 920);
    });
  }, [selectedDriverId]);

  // Fit bounds ONLY when pickup or dropoff coordinates genuinely change.
  // We do NOT include drivers or map drag/zoom here, preventing the map from
  // automatically reverting its position while the user explores.
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const prev = prevCoordsRef.current;
    const pLat = getLat(pickupCoordinates);
    const pLng = getLng(pickupCoordinates);
    const dLat = getLat(dropoffCoordinates);
    const dLng = getLng(dropoffCoordinates);

    const pickupChanged = pLat !== prev.pickupLat || pLng !== prev.pickupLng;
    const dropoffChanged = dLat !== prev.dropoffLat || dLng !== prev.dropoffLng;

    if (pickupChanged || dropoffChanged) {
      prevCoordsRef.current = {
        pickupLat: pLat,
        pickupLng: pLng,
        dropoffLat: dLat,
        dropoffLng: dLng,
      };
      // Reset user interaction lock when a new location is picked
      userInteractedRef.current = false;
      const id = setTimeout(() => {
        applyBoundsToMap(map);
      }, 60);
      return () => clearTimeout(id);
    }
  }, [map, pickupCoordinates, dropoffCoordinates]);

  const handleRecenter = () => {
    userInteractedRef.current = false;
    const liveMap = mapInstanceRef.current || map;
    if (liveMap && window.google?.maps) {
      window.google.maps.event.trigger(liveMap, 'resize');
      applyBoundsToMap(liveMap);
    } else {
      applyBoundsToMap();
    }
  };

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const mapContent = (
    <div
      ref={mapContainerRef}
      style={{ willChange: 'transform', transform: 'translateZ(0)' }}
      className={`relative w-full ${isFullscreen ? 'h-full flex-1 min-h-0' : 'h-[260px]'}`}
    >
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          onDragStart={() => {
            userInteractedRef.current = true;
          }}
          onZoomChanged={() => {
            if (!isProgrammaticMoveRef.current) {
              userInteractedRef.current = true;
            }
          }}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            styles: uberMapStyle,
          }}
        >
          {/* Pickup, dropoff, drivers, and route polyline are rendered via native imperative effects */}
        </GoogleMap>
      ) : (
        <div className="w-full h-full bg-gray-100" />
      )}

      {(distanceKm != null || durationMin != null) && (
        <div className="absolute top-2 left-2 z-[5] flex items-center gap-1.5">
          {distanceKm != null && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-green-100 text-green-700 shadow-md border border-green-200">
              {distanceKm.toFixed(1)} km
            </span>
          )}
          {durationMin != null && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-700 shadow-md border border-purple-200">
              {Math.round(durationMin)} min
            </span>
          )}
        </div>
      )}

      {!isFullscreen && (
        <div className="absolute top-2 right-2 z-[5] flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleRecenter}
            className="p-2 bg-white hover:bg-gray-50 rounded shadow-lg transition-colors border border-gray-200 text-gray-700 cursor-pointer"
            title="Re-center map"
          >
            <LocateFixed className="h-4 w-4 text-gray-700" />
          </button>
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className="p-2 bg-white hover:bg-gray-50 rounded shadow-lg transition-colors border border-gray-200 text-gray-700 cursor-pointer"
            title="Fullscreen"
          >
            <Maximize2 className="h-4 w-4 text-gray-700" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        {!isFullscreen ? (
          <>
            {mapContent}
            <CommandCenterMapLegend />
          </>
        ) : (
          <div className="h-[295px] flex flex-col items-center justify-center bg-gray-50 text-xs text-gray-400 gap-2 border border-gray-100 rounded-2xl">
            <span>Map is currently open in fullscreen</span>
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-200 rounded-md hover:bg-blue-50 cursor-pointer"
            >
              Exit Fullscreen
            </button>
          </div>
        )}
      </div>

      {isFullscreen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 sm:p-6"
            onClick={() => setIsFullscreen(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">Booking Map</h3>
                  <span className="text-xs text-gray-400">Fullscreen view</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRecenter}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-medium text-gray-700 transition-colors cursor-pointer"
                    title="Re-center map"
                  >
                    <LocateFixed className="w-3.5 h-3.5 text-gray-600" />
                    <span>Re-center</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFullscreen(false)}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    title="Close (Esc)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Map body */}
              <div className="flex-1 min-h-0 relative">
                {mapContent}
              </div>

              {/* Legend footer */}
              <div className="shrink-0 border-t border-gray-100 bg-white">
                <CommandCenterMapLegend />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CommandCenterBookingMapCard;
